
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Coins, Gift, Sparkles, Star, Trophy } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';

interface Reward {
  id: string;
  type: 'xp';
  name: string;
  icon: React.ReactNode;
  value: {
    amount?: number;
  };
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  weight: number;
}

const rewards: Reward[] = [
  {
    id: 'xp-small',
    type: 'xp',
    name: '50 XP',
    icon: <Star className="h-4 w-4" />,
    value: { amount: 50 },
    rarity: 'common',
    weight: 40
  },
  {
    id: 'xp-medium',
    type: 'xp',
    name: '100 XP',
    icon: <Sparkles className="h-4 w-4" />,
    value: { amount: 100 },
    rarity: 'rare',
    weight: 35
  },
  {
    id: 'xp-large',
    type: 'xp',
    name: '200 XP',
    icon: <Trophy className="h-4 w-4" />,
    value: { amount: 200 },
    rarity: 'epic',
    weight: 20
  },
  {
    id: 'xp-huge',
    type: 'xp',
    name: '500 XP',
    icon: <Trophy className="h-4 w-4" />,
    value: { amount: 500 },
    rarity: 'legendary',
    weight: 5
  }
];

export const VintageSlotMachine = () => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [currentReward, setCurrentReward] = useState<Reward | null>(null);
  const [showReward, setShowReward] = useState(false);
  const [canSpin, setCanSpin] = useState(true);
  const [dailySpins, setDailySpins] = useState(0);
  const { toast } = useToast();
  const { user } = useAuth();

  const maxDailySpins = 3;

  const selectReward = (): Reward => {
    const totalWeight = rewards.reduce((sum, reward) => sum + reward.weight, 0);
    let random = Math.random() * totalWeight;
    
    for (const reward of rewards) {
      random -= reward.weight;
      if (random <= 0) {
        return reward;
      }
    }
    
    return rewards[0];
  };

  const applyReward = async (reward: Reward) => {
    if (!user) return;

    try {
      const { error } = await supabase.functions.invoke('apply-reward', {
        body: { reward }
      });

      if (error) {
        console.error('Error applying reward:', error);
        toast({
          title: 'Error',
          description: 'Failed to apply reward. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      // Dispatch custom event for reward system
      window.dispatchEvent(new CustomEvent('rewardClaimed', { 
        detail: reward 
      }));

      toast({
        title: 'Reward Earned!',
        description: `You received ${reward.name}!`,
      });

    } catch (error) {
      console.error('Error applying reward:', error);
      toast({
        title: 'Error',
        description: 'Failed to apply reward. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSpin = async () => {
    if (!canSpin || !user) return;

    setIsSpinning(true);
    setShowReward(false);
    setCurrentReward(null);

    // Simulate spinning animation
    setTimeout(() => {
      const reward = selectReward();
      setCurrentReward(reward);
      setIsSpinning(false);
      setShowReward(true);
      
      // Apply the reward
      applyReward(reward);
      
      // Update daily spins count
      setDailySpins(prev => prev + 1);
      setCanSpin(dailySpins + 1 < maxDailySpins);
    }, 2000);
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'rare': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'epic': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'legendary': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <Card className="win95-container border-2 border-black bg-win95-gray">
      <CardHeader className="win95-title-bar p-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Coins className="h-5 w-5" />
          XP Booster
          <Badge variant="outline" className="ml-auto">
            {dailySpins}/{maxDailySpins} spins today
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="text-center space-y-4">
          <div className="h-32 flex items-center justify-center border-2 border-black bg-black relative overflow-hidden">
            <AnimatePresence mode="wait">
              {isSpinning ? (
                <motion.div
                  key="spinning"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-green-400 text-2xl font-mono"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 0.5, repeat: Infinity, ease: "linear" }}
                  >
                    <Gift className="h-8 w-8" />
                  </motion.div>
                </motion.div>
              ) : showReward && currentReward ? (
                <motion.div
                  key="reward"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  className="text-center text-green-400"
                >
                  <div className="text-2xl mb-2">
                    {currentReward.icon}
                  </div>
                  <div className="text-xs font-mono">
                    {currentReward.name}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-green-400 text-xl font-mono"
                >
                  <Gift className="h-8 w-8" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {showReward && currentReward && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="space-y-2"
            >
              <Badge className={getRarityColor(currentReward.rarity)}>
                {currentReward.rarity.toUpperCase()}
              </Badge>
              <p className="text-sm font-medium">{currentReward.name}</p>
            </motion.div>
          )}

          <Button 
            onClick={handleSpin}
            disabled={!canSpin || isSpinning}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 border-2 border-black"
          >
            {isSpinning ? 'PROCESSING...' : canSpin ? 'SPIN' : 'DAILY LIMIT REACHED'}
          </Button>

          <div className="text-xs text-gray-600 space-y-1">
            <p>Earn XP rewards to boost your progress!</p>
            <p>Limited to {maxDailySpins} spins per day</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
